import React, { useState, useRef, useEffect } from 'react';
import {
  EyeOff,
  ChevronUp,
  ChevronDown,
  Palette,
  Shapes,
  Focus
} from 'lucide-react';
import { NodeCustomization } from '../types';

interface NodeContextMenuProps {
  nodeId: string;
  nodeName: string;
  isExpanded: boolean;
  hasChildren: boolean;
  customization?: NodeCustomization;
  isOpen: boolean;
  onToggleExpand: (nodeId: string) => void;
  onToggleHide: (nodeId: string) => void;
  onCustomizeNode: (nodeId: string, customization: NodeCustomization) => void;
  onIsolateTree: (nodeId: string) => void;
  onClose: () => void;
}

const NodeContextMenu: React.FC<NodeContextMenuProps> = ({
  nodeId,
  nodeName: _nodeName,
  isExpanded,
  hasChildren,
  customization,
  isOpen,
  onToggleExpand,
  onToggleHide,
  onCustomizeNode,
  onIsolateTree,
  onClose,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showShapePicker, setShowShapePicker] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top?: string; bottom?: string; right?: string; left?: string }>({ top: '100%', right: '8px' });
  const [submenuSide, setSubmenuSide] = useState<'right' | 'left'>('right');
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
        setShowColorPicker(false);
        setShowShapePicker(false);
      }
    };

    if (isOpen) {
      // Use both mousedown and click events to ensure it works
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Adjust position to stay within viewport
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const newPosition: typeof menuPosition = { top: '100%', right: '8px' };

      // Check if menu goes below viewport
      if (rect.bottom > window.innerHeight) {
        delete newPosition.top;
        newPosition.bottom = '100%';
      }

      // Check if menu goes past right edge
      if (rect.right > window.innerWidth) {
        delete newPosition.right;
        newPosition.left = '8px';
      }

      // Check if submenu would go past right edge
      if (rect.right + 180 > window.innerWidth) {
        setSubmenuSide('left');
      } else {
        setSubmenuSide('right');
      }

      setMenuPosition(newPosition);
    }
  }, [isOpen]);

  const handleMenuItemClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
    // Don't close menu - let user perform multiple actions
    // Menu closes only when clicking outside or clicking another node
  };

  const colorOptions = [
    { name: 'Standaard', value: undefined },
    { name: 'Blauw', value: '#3b82f6' },
    { name: 'Groen', value: '#10b981' },
    { name: 'Rood', value: '#ef4444' },
    { name: 'Paars', value: '#8b5cf6' },
    { name: 'Oranje', value: '#f59e0b' },
    { name: 'Roze', value: '#ec4899' },
    { name: 'Teal', value: '#14b8a6' },
  ];

  const shapeOptions = [
    { name: 'Default', value: 'rounded' as const, icon: '▭' },
    { name: 'Ovaal', value: 'oval' as const, icon: '⬭' },
    { name: 'Driehoek', value: 'triangle' as const, icon: '▲' },
  ];

  if (!isOpen) return null;

  return (
    <div 
      ref={menuRef}
      className="node-context-menu"
      style={{
        position: 'absolute',
        ...menuPosition,
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        minWidth: '200px',
        padding: '8px 0',
        marginTop: menuPosition.top ? '4px' : undefined,
        marginBottom: menuPosition.bottom ? '4px' : undefined,
      }}
    >
          {/* Hide Option */}
          <button
            onClick={(e) => handleMenuItemClick(e, () => onToggleHide(nodeId))}
            className="menu-item"
            style={{
              width: '100%',
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#374151',
              transition: 'background 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            <EyeOff size={16} />
            Entiteit & dochters verbergen
          </button>

          {/* Expand/Collapse Option */}
          {hasChildren && (
            <button
              onClick={(e) => handleMenuItemClick(e, () => onToggleExpand(nodeId))}
              className="menu-item"
              style={{
                width: '100%',
                padding: '8px 16px',
                border: 'none',
                background: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#374151',
                transition: 'background 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
              }}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {isExpanded ? 'Dochters inklappen' : 'Dochters uitklappen'}
            </button>
          )}

          {/* Divider */}
          <div style={{ height: '1px', background: '#e5e7eb', margin: '8px 0' }} />

          {/* Color Option */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowColorPicker(!showColorPicker);
              }}
              className="menu-item"
              style={{
                width: '100%',
                padding: '8px 16px',
                border: 'none',
                background: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#374151',
                transition: 'background 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
              }}
            >
              <Palette size={16} />
              Kleur wijzigen
              <span style={{ marginLeft: 'auto', fontSize: '12px' }}>▶</span>
            </button>

            {showColorPicker && (
              <div
                style={{
                  position: 'absolute',
                  ...(submenuSide === 'right' ? { left: '100%' } : { right: '100%' }),
                  top: '0',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  padding: '8px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '4px',
                  minWidth: '160px'
                }}
              >
                {colorOptions.map((color) => (
                  <button
                    key={color.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCustomizeNode(nodeId, { ...customization, color: color.value });
                      setShowColorPicker(false);
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      background: color.value || '#f8f9fa',
                      color: color.value ? 'white' : '#374151',
                      cursor: 'pointer',
                      fontSize: '12px',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    {color.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Shape Option */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowShapePicker(!showShapePicker);
              }}
              className="menu-item"
              style={{
                width: '100%',
                padding: '8px 16px',
                border: 'none',
                background: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#374151',
                transition: 'background 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
              }}
            >
              <Shapes size={16} />
              Vorm wijzigen
              <span style={{ marginLeft: 'auto', fontSize: '12px' }}>▶</span>
            </button>

            {showShapePicker && (
              <div
                style={{
                  position: 'absolute',
                  ...(submenuSide === 'right' ? { left: '100%' } : { right: '100%' }),
                  top: '0',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  padding: '8px 0',
                  minWidth: '180px'
                }}
              >
                {shapeOptions.map((shape) => (
                  <button
                    key={shape.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCustomizeNode(nodeId, { ...customization, shape: shape.value });
                      setShowShapePicker(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      border: 'none',
                      background: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#374151',
                      transition: 'background 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'none';
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>{shape.icon}</span>
                    {shape.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ height: '1px', background: '#e5e7eb', margin: '8px 0' }} />

          {/* Isolate Tree Option */}
          <button
            onClick={(e) => handleMenuItemClick(e, () => onIsolateTree(nodeId))}
            className="menu-item"
            style={{
              width: '100%',
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#374151',
              transition: 'background 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            <Focus size={16} />
            Boom isoleren
          </button>
    </div>
  );
};

export default NodeContextMenu;



